import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type Transaction, type ExpectedRefund } from '../api.ts';
import { useApp, useFetch, useMoney, usePrivateMoney } from '../store.tsx';
import { PullToRefresh } from '../PullToRefresh.tsx';
import {
  Card, Empty, ErrorState, Field, ProvenanceBadge, ConfidenceDot, Sheet,
  Skeleton, TransactionRow, SectionTitle,
} from '../components.tsx';
import { formatDate, formatMoney, minorToInput, parseAmountInput, signedAmount } from '../format.ts';

/* ============================================================================
   Activity list, transaction detail, manual entry, and the review queue.
   ========================================================================= */

/**
 * How far back the Activity list reaches before being asked to go further.
 *
 * Long enough to cover a full pay cycle plus the days either side of it that
 * people actually look back over, short enough that the list is a fixed cost
 * however many years of history an account has. Nothing is deleted — this is
 * what the screen asks for, not what is kept.
 */
const ACTIVITY_WINDOW_DAYS = 60;

/**
 * Rows fetched per request.
 *
 * The list used to send this and stop there, with no offset and no way to ask
 * for more — so "Show everything" lifted the date filter and then returned the
 * same first hundred rows, changing nothing on screen. A window is only half a
 * pagination; without the other half it is just a smaller cap wearing a label.
 */
const PAGE_SIZE = 100;

interface Filters {
  search: string;
  category: string;
  type: string;
  merchant: string;
}

const EMPTY_FILTERS: Filters = { search: '', category: '', type: '', merchant: '' };

export function TransactionsScreen({
  initialCategory, reviewOnly, onReviewOnlyChange, onOpen, onAdd,
}: {
  initialCategory?: string;
  /** Narrowed to the transactions the pipeline could not finish. */
  reviewOnly: boolean;
  onReviewOnlyChange: (v: boolean) => void;
  onOpen: (id: string) => void;
  onAdd: () => void;
}): JSX.Element {
  const { t, locale, categories, activeAccountId } = useApp();
  const ar = locale === 'ar';
  const [filters, setFilters] = useState<Filters>({ ...EMPTY_FILTERS, category: initialCategory ?? '' });
  const [showFilters, setShowFilters] = useState(false);
  const [debounced, setDebounced] = useState(filters.search);
  const [showAll, setShowAll] = useState(false);
  // Pages beyond the first, appended. The first page stays with useFetch so
  // filters, search and pull-to-refresh keep working exactly as they did.
  const [more, setMore] = useState<Transaction[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(filters.search), 300);
    return () => window.clearTimeout(id);
  }, [filters.search]);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: String(PAGE_SIZE) });
    // A window rather than everything, so the list stays a fixed cost as a
    // ledger grows. Searching drops it: typing a merchant name means looking
    // for something specific, and finding nothing because it happened five
    // weeks ago would read as the search being broken.
    if (!debounced && !showAll) {
      p.set('from', new Date(Date.now() - ACTIVITY_WINDOW_DAYS * 86_400_000).toISOString());
    }
    if (debounced) p.set('search', debounced);
    if (filters.category) p.set('category', filters.category);
    if (filters.type) p.set('type', filters.type);
    if (filters.merchant) p.set('merchant', filters.merchant);
    if (activeAccountId) p.set('accountId', activeAccountId);
    return `/transactions?${p.toString()}`;
  }, [debounced, showAll, filters.category, filters.type, filters.merchant, activeAccountId]);

  const { data, error, loading, reload } = useFetch<{
    transactions: Transaction[];
    pagination: { total: number };
    totals: { spendingMinor: number };
  }>(query, [query]);

  // A new query is a new list. Keeping pages fetched under the old filters
  // would mix results from two different questions.
  useEffect(() => { setMore([]); }, [query]);

  const rows = useMemo(
    () => [...(data?.transactions ?? []), ...more],
    [data, more],
  );
  const total = data?.pagination.total ?? 0;
  const hasMore = rows.length < total;

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = await api.get<{ transactions: Transaction[] }>(
        `${query}&offset=${rows.length}`,
      );
      // Concurrent reloads can overlap; keying by id means a row can never be
      // appended twice even if two pages overlap.
      setMore((prev) => {
        const seen = new Set([...(data?.transactions ?? []), ...prev].map((t) => t.id));
        return [...prev, ...next.transactions.filter((t) => !seen.has(t.id))];
      });
    } catch {
      // Silent: the page below simply does not grow, and the button stays.
    } finally {
      setLoadingMore(false);
    }
  };

  const { data: review, reload: reloadReview } = useFetch<{ transactions: Transaction[]; failedEvents: unknown[] }>('/transactions/review/queue');

  // The review queue too — it sits at the top of this screen, so refreshing
  // the list beneath it while leaving it stale would look answered when it
  // is not.
  const refreshAll = useCallback(
    () => Promise.all([reload(), reloadReview()]),
    [reload, reloadReview],
  );

  // Group by day so the list reads as a timeline rather than a wall of rows.
  const grouped = useMemo(() => {
    const map = new Map<string, Transaction[]>();
    for (const tx of rows) {
      const key = tx.occurredAt.slice(0, 10);
      const list = map.get(key) ?? [];
      list.push(tx);
      map.set(key, list);
    }
    return [...map.entries()];
  }, [rows]);

  // Clearing the last flagged transaction hides the banner, and the banner is
  // what carries the way back — so without this the reader is left on an empty
  // screen with no control on it.
  useEffect(() => {
    if (reviewOnly && review && review.transactions.length === 0) onReviewOnlyChange(false);
  }, [reviewOnly, review, onReviewOnlyChange]);

  const activeFilterCount = [filters.category, filters.type, filters.merchant].filter(Boolean).length;

  return (
    <PullToRefresh onRefresh={refreshAll}>
    <div className="screen">
      <div className="row row--between" style={{ marginBottom: 12 }}>
        <h1 style={{ fontSize: 21, fontWeight: 680 }}>{t('transactions')}</h1>
        <button className="btn btn--secondary btn--sm" onClick={onAdd}>+ {t('addTransaction')}</button>
      </div>

      <div className="row" style={{ gap: 8, marginBottom: 12 }}>
        <input
          className="input grow"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          placeholder={t('search')}
          type="search"
        />
        <button className={`chip${activeFilterCount ? ' chip--active' : ''}`} onClick={() => setShowFilters(true)}>
          {t('filters')}{activeFilterCount ? ` (${activeFilterCount})` : ''}
        </button>
      </div>

      {/* The banner is a count and a way in. "See all" used to clear the
          filters, which on an unfiltered screen did nothing at all — so the
          one control pointing at the flagged transactions led nowhere, and the
          user was left to find them among everything else. */}
      {review?.transactions.length ? (
        <Card style={{ borderColor: 'var(--warning)', marginBottom: 12 }}>
          <div className="row" style={{ gap: 10 }}>
            <span aria-hidden>⚠️</span>
            <span className="grow small">
              <span className="strong">{t('reviewQueue')}</span>
              <span className="muted"> · {review.transactions.length}</span>
            </span>
            <button className="btn btn--ghost btn--sm"
                    onClick={() => onReviewOnlyChange(!reviewOnly)}>
              {reviewOnly
                ? (ar ? 'عرض الكل' : 'Show everything')
                : t('seeAll')}
            </button>
          </div>
        </Card>
      ) : null}

      {/* Shown from the queue itself rather than by filtering the list to
          needs_review: the banner's count comes from that endpoint, and a list
          built from a different query could disagree with the number that sent
          the user here. */}
      {reviewOnly && review && (
        <>
          <SectionTitle>{ar ? 'بحاجة إلى انتباهك' : 'Needs your attention'}</SectionTitle>
          <Card className="card--flush">
            {review.transactions.map((tx) => (
              <div key={tx.id} className="flagged">
                <TransactionRow tx={tx} onClick={() => onOpen(tx.id)} />
                {/* Why it was flagged, said where it is being read. Without it
                    a row looks ordinary and the only way to find out what is
                    wrong with it is to open it. */}
                {tx.needsReviewReason && (
                  <div className="tiny flagged__why">{tx.needsReviewReason}</div>
                )}
                {/* A transfer is the one thing here the app cannot decide for
                    itself. Everything else in this list is a parse it got
                    wrong; this is a question only the account holder can
                    answer, so it is asked inline rather than behind a tap. */}
                {tx.transactionType === 'transfer' && !tx.transferCountsAs && (
                  <TransferQuestion tx={tx} onAnswered={refreshAll} />
                )}
              </div>
            ))}
          </Card>
          <p className="tiny muted" style={{ marginTop: 10, lineHeight: 1.6 }}>
            {ar
              ? 'افتح أي حركة لتصحيحها أو لتأكيد أنها صحيحة. تختفي من هنا بمجرد ذلك.'
              : 'Open one to correct it, or to confirm it is right. It leaves this list either way.'}
          </p>
        </>
      )}

      {!reviewOnly && (
        <>
          {loading && !data && <Skeleton count={5} />}
          {error && <ErrorState message={error} onRetry={reload} />}

          {data && data.transactions.length === 0 && (
            <Empty icon="🔍" title={t('noTransactions')} body={t('noTransactionsBody')} />
          )}

          {grouped.map(([day, items]) => (
            <div key={day}>
              <SectionTitle>{formatDate(`${day}T12:00:00Z`, locale, { relative: true })}</SectionTitle>
              <Card className="card--flush">
                {items.map((tx) => <TransactionRow key={tx.id} tx={tx} onClick={() => onOpen(tx.id)} />)}
              </Card>
            </div>
          ))}
        </>
      )}

      {/* Says what is on screen and what is not, in counts rather than in
          promises. It used to claim "nothing is deleted", which is a statement
          about retention this screen is in no position to make — and would
          quietly become untrue the day a retention policy exists. What it can
          honestly report is how much of the total it is showing. */}
      {data && rows.length > 0 && (
        <div className="center" style={{ marginTop: 16 }}>
          {hasMore && (
            <button className="btn btn--secondary btn--sm" disabled={loadingMore} onClick={loadMore}>
              {loadingMore
                ? '…'
                : (locale === 'ar' ? 'تحميل المزيد' : 'Load more')}
            </button>
          )}
          <p className="tiny muted" style={{ marginTop: hasMore ? 10 : 0, lineHeight: 1.6 }}>
            {locale === 'ar'
              ? `${rows.length} من ${total}${showAll || debounced ? '' : ` · آخر ${ACTIVITY_WINDOW_DAYS} يوماً`}. `
              : `${rows.length} of ${total}${showAll || debounced ? '' : `, from the last ${ACTIVITY_WINDOW_DAYS} days`}. `}
            {!showAll && !debounced && (
              <button className="tiny" style={{ color: 'var(--accent-text)', background: 'none', padding: 0, fontWeight: 620 }}
                      onClick={() => setShowAll(true)}>
                {locale === 'ar' ? 'عرض كل السجل' : 'Search all history'}
              </button>
            )}
          </p>
        </div>
      )}

      {showFilters && (
        <Sheet title={t('filters')} onClose={() => setShowFilters(false)}>
          <div className="stack">
            <Field label={t('category')}>
              <select className="select" value={filters.category}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
                <option value="">{t('none')}</option>
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>{locale === 'ar' ? c.ar : c.en}</option>
                ))}
              </select>
            </Field>

            <Field label={t('type')}>
              <select className="select" value={filters.type}
                      onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
                <option value="">{t('none')}</option>
                <option value="expense">{t('typeExpense')}</option>
                <option value="income">{t('typeIncome')}</option>
                <option value="transfer">{t('typeTransfer')}</option>
                <option value="refund">{t('typeRefund')}</option>
                <option value="fee">{t('typeFee')}</option>
              </select>
            </Field>

            <div className="row" style={{ gap: 10, marginTop: 6 }}>
              <button className="btn btn--secondary grow" onClick={() => setFilters({ ...EMPTY_FILTERS })}>
                {t('clearFilters')}
              </button>
              <button className="btn btn--primary grow" onClick={() => setShowFilters(false)}>{t('done')}</button>
            </div>
          </div>
        </Sheet>
      )}
    </div>
    </PullToRefresh>
  );
}

// ── Transaction detail ───────────────────────────────────────────────────────

interface MerchantSubscription {
  merchant: string;
  state: 'tracked' | 'dismissed' | 'one_off' | 'untracked';
  frequency: string | null;
  nextExpected: string | null;
  amountMinor: number | null;
  currency: string | null;
}

interface DetailResponse {
  transaction: Record<string, unknown>;
  related: { id: string; amountMinor: number; occurredAt: string; category: string | null }[];
  /** Null for income and for a charge with no merchant to track. */
  subscription: MerchantSubscription | null;
  /** Open expectations against this purchase — a return, a share, or both. */
  expectations: ExpectedRefund[];
}

/**
 * "Does this transfer count?"
 *
 * A transfer says how the money moved, not what it meant. AED 5,000 into
 * savings is not spending; AED 500 to a friend is, in every sense that matters
 * to "what can I spend today". The app cannot tell them apart from the message,
 * so it used to count neither — which quietly understated spending for anybody
 * who sends money to people.
 *
 * The direction is read from the message, so the question is a yes or no rather
 * than a choice of two. Where the message did not say, both are offered: a
 * guess here would file somebody's savings as earnings.
 *
 * The rule is remembered against the other side of the transfer, never against
 * transfers in general. "Always for AHMED K" is something somebody can mean;
 * "always for transfers" would turn every savings top-up into spending.
 */
function TransferQuestion({ tx, onAnswered }: {
  tx: Transaction;
  onAnswered: () => void;
}): JSX.Element {
  const { locale, toast, toastError } = useApp();
  const ar = locale === 'ar';
  const [always, setAlways] = useState(false);
  const [busy, setBusy] = useState(false);

  /*
   * Who the answer is about.
   *
   * A name when the bank gave one. Otherwise the beneficiary's account suffix,
   * because rent, a contractor and family abroad are all paid to an account
   * number with no name anywhere in the message — and those are usually the
   * largest transfers a statement has.
   */
  const named = tx.merchantRaw
    || (tx.transferCounterpartyRef
        ? (ar ? `الحساب المنتهي بـ ${tx.transferCounterpartyRef}` : `account ending ${tx.transferCounterpartyRef}`)
        : null);

  /*
   * Most transfers name nobody at all — "AED 500.00 debited for a fund
   * transfer" and no more. The question still has to be asked, so it is
   * phrased about the transfer itself, and "always" is offered for that whole
   * class rather than for a counterparty that does not exist. Without it,
   * somebody who transfers often would answer the same question for ever.
   */
  const heading = named
    ? (ar ? `تحويل مع ${named}. هل يُحتسب ضمن ميزانيتك؟`
          : `Transfer with ${named}. Should it count towards your budget?`)
    : (ar ? 'تحويل بدون اسم. هل يُحتسب ضمن ميزانيتك؟'
          : 'Transfer with no name on it. Should it count towards your budget?');

  const alwaysLabel = named
    ? (ar ? `افعل هذا دائمًا مع ${named}` : `Always do this for ${named}`)
    : (ar ? 'افعل هذا دائمًا مع التحويلات بدون اسم' : 'Always do this for transfers with no name');

  const answer = async (countsAs: 'expense' | 'income' | 'ignored') => {
    setBusy(true);
    try {
      const res = await api.post<{ applied: number }>(
        `/transactions/${tx.id}/transfer-treatment`,
        { countsAs, alwaysForCounterparty: always },
      );
      if (always && res.applied > 1) {
        toast(ar
          ? `طُبّق على ${res.applied} تحويلات`
          : `Applied to ${res.applied} transfers`);
      }
      onAnswered();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const outgoing = tx.transferDirection === 'out';
  const incoming = tx.transferDirection === 'in';

  return (
    <div className="flagged__why" style={{ color: 'var(--text-secondary)' }}>
      <div style={{ lineHeight: 1.5 }}>{heading}</div>

      <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        {/* Only the direction the message actually reported. Offering both when
            the bank already said which way it went invites the wrong one. */}
        {!incoming && (
          <button className="btn btn--secondary btn--sm" disabled={busy}
                  onClick={() => void answer('expense')}>
            {ar ? 'نعم، مصروف' : 'Yes, money out'}
          </button>
        )}
        {!outgoing && (
          <button className="btn btn--secondary btn--sm" disabled={busy}
                  onClick={() => void answer('income')}>
            {ar ? 'نعم، دخل' : 'Yes, money in'}
          </button>
        )}
        <button className="btn btn--ghost btn--sm" disabled={busy}
                onClick={() => void answer('ignored')}>
          {ar ? 'لا تحتسبه' : "No, don't count it"}
        </button>
      </div>

      <label className="row" style={{ gap: 8, marginTop: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={always} disabled={busy}
               onChange={(e) => setAlways(e.target.checked)}
               style={{ width: 16, height: 16, accentColor: 'var(--accent)' }} />
        <span className="tiny">{alwaysLabel}</span>
      </label>
    </div>
  );
}

export function TransactionDetail({ id, onClose, onChanged }: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element {
  const { t, locale, categories, categoryLabel, subcategoryLabel, toast, toastError } = useApp();
  const money = useMoney();
  const privateMoney = usePrivateMoney();
  const { data, loading, reload } = useFetch<DetailResponse>(`/transactions/${id}`, [id]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const tx = data?.transaction as (Record<string, unknown> & {
    id: string; amount_minor: number; currency: string; transaction_type: string;
    merchant_raw: string | null; merchant_display: string | null; description: string | null;
    category: string | null; subcategory: string | null; category_confidence: number | null;
    category_method: string | null; category_reason: string | null; is_ai_categorized: boolean;
    user_corrected: boolean; occurred_at: string; card_last4: string | null;
    payment_method: string | null;
    account_name: string | null; source: string; status: string; needs_review_reason: string | null;
    parse_confidence: number | null; notes: string | null;
    raw_body: string | null; raw_purged: boolean; raw_source: string | null;
  }) | undefined;

  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [applyToSimilar, setApplyToSimilar] = useState(false);

  useEffect(() => {
    if (tx) { setCategory(tx.category ?? ''); setSubcategory(tx.subcategory ?? ''); }
  }, [tx?.id]);

  const save = async () => {
    if (!tx) return;
    setBusy(true);
    try {
      const res = await api.patch<{ similarUpdated: number }>(`/transactions/${tx.id}`, {
        category, subcategory: subcategory || null, applyToSimilar,
      });
      toast(res.similarUpdated > 0
        ? `${t('done')} · ${res.similarUpdated} ${t('transactionsLower')}`
        : t('done'));
      setEditing(false);
      reload();
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (action: 'confirm' | 'mark-duplicate') => {
    setBusy(true);
    try {
      await api.post(`/transactions/${id}/${action}`);
      toast(t('done'));
      reload();
      onChanged();
      if (action === 'mark-duplicate') onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/transactions/${id}`);
      toast(t('done'));
      onChanged();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const selectedCategory = categories.find((c) => c.key === category);

  return (
    <Sheet title={t('transactionDetails')} onClose={onClose}>
      {loading && <Skeleton count={3} />}
      {tx && (
        <div className="stack">
          <div className="center" style={{ paddingBlock: 8 }}>
            <div style={{
              fontSize: 32, fontWeight: 690, letterSpacing: '-0.03em',
              fontVariantNumeric: 'tabular-nums',
              color: signedAmount(tx.amount_minor, tx.transaction_type) > 0 ? 'var(--positive)' : 'var(--text)',
            }}>
              {/* Covered for a salary credit, as in the list it came from. */}
              {(tx.transaction_type === 'income' ? privateMoney : money)(tx.amount_minor, tx.currency)}
            </div>
            <div className="muted" style={{ marginTop: 4, fontSize: 15 }}>
              {tx.merchant_display || tx.merchant_raw || '—'}
            </div>
            <div className="tiny muted" style={{ marginTop: 3 }}>
              {formatDate(tx.occurred_at, locale, { withTime: true })}
            </div>
          </div>

          {tx.status === 'needs_review' && (
            <Card style={{ borderColor: 'var(--warning)' }}>
              <div className="small" style={{ marginBottom: 10 }}>
                <span className="badge badge--warning">{t('needsReview')}</span>
                <div style={{ marginTop: 8 }}>{tx.needs_review_reason}</div>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn btn--secondary btn--sm grow" disabled={busy} onClick={() => confirm('confirm')}>
                  {t('confirmCorrect')}
                </button>
                <button className="btn btn--danger btn--sm grow" disabled={busy} onClick={() => confirm('mark-duplicate')}>
                  {t('markDuplicate')}
                </button>
              </div>
            </Card>
          )}

          <Card>
            <DetailRow label={t('type')} value={t(`type${tx.transaction_type.charAt(0).toUpperCase() + tx.transaction_type.slice(1)}` as never)} />
            <DetailRow label={t('category')} value={
              <span>
                {categoryLabel(tx.category)}
                {tx.subcategory && <span className="muted"> · {subcategoryLabel(tx.category, tx.subcategory)}</span>}
              </span>
            } />
            {tx.account_name && <DetailRow label={t('account')} value={tx.account_name} />}
            {/* The card number already appears under Account; this row is for
                the method itself (card / cash / transfer). */}
            {tx.payment_method && tx.payment_method !== 'unknown' && (
              <DetailRow label={t('paymentMethod')} value={PAYMENT_METHOD_LABELS[tx.payment_method]?.[locale] ?? tx.payment_method} />
            )}
            <DetailRow label={t('source')} value={tx.source} />
            {tx.notes && <DetailRow label={t('notes')} value={tx.notes} />}
          </Card>

          {/* Splitting and refund watch — both are judgements about a charge
              rather than facts of it, so they live below the facts. */}
          {tx.transaction_type === 'expense' && (
            <SplitAndRefundCard
              tx={tx}
              expectations={data?.expectations ?? []}
              onChanged={() => { reload(); onChanged(); }}
            />
          )}

          {data?.subscription && (
            <SubscriptionCard
              sub={data.subscription}
              merchantDisplay={tx.merchant_display || tx.merchant_raw || data.subscription.merchant}
              onChanged={() => { reload(); onChanged(); }}
            />
          )}

          {/* How the category was decided — always inspectable, never a black box. */}
          <Card>
            <div className="field__label" style={{ marginBottom: 8 }}>{t('howCategorized')}</div>
            <div className="row row--wrap" style={{ gap: 8, marginBottom: 8 }}>
              {tx.user_corrected
                ? <span className="badge badge--confirmed">{locale === 'ar' ? 'اخترتها بنفسك' : 'You chose this'}</span>
                : tx.is_ai_categorized
                  ? <ProvenanceBadge kind="ai" />
                  : <ProvenanceBadge kind="inferred" />}
              <ConfidenceDot value={tx.category_confidence} />
            </div>
            <div className="small muted">{tx.category_reason}</div>
            {tx.parse_confidence != null && tx.parse_confidence < 1 && (
              <div className="tiny muted" style={{ marginTop: 8 }}>
                {locale === 'ar' ? 'دقة قراءة الرسالة' : 'Message read accuracy'}: {Math.round(tx.parse_confidence * 100)}%
              </div>
            )}
          </Card>

          {editing ? (
            <Card>
              <div className="stack">
                <Field label={t('category')}>
                  <select className="select" value={category} onChange={(e) => { setCategory(e.target.value); setSubcategory(''); }}>
                    {categories.map((c) => (
                      <option key={c.key} value={c.key}>{locale === 'ar' ? c.ar : c.en}</option>
                    ))}
                  </select>
                </Field>

                {selectedCategory && selectedCategory.subcategories.length > 0 && (
                  <Field label={t('subcategory')}>
                    <select className="select" value={subcategory} onChange={(e) => setSubcategory(e.target.value)}>
                      <option value="">—</option>
                      {selectedCategory.subcategories.map((s) => (
                        <option key={s.key} value={s.key}>{locale === 'ar' ? s.ar : s.en}</option>
                      ))}
                    </select>
                  </Field>
                )}

                <label className="row" style={{ gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input type="checkbox" checked={applyToSimilar}
                         onChange={(e) => setApplyToSimilar(e.target.checked)}
                         style={{ marginTop: 3, width: 18, height: 18, accentColor: 'var(--accent)' }} />
                  <span>
                    <span className="small strong" style={{ display: 'block' }}>{t('applyToSimilar')}</span>
                    <span className="tiny muted">{t('applyToSimilarHelp')}</span>
                  </span>
                </label>

                <div className="row" style={{ gap: 8 }}>
                  <button className="btn btn--secondary grow" onClick={() => setEditing(false)}>{t('cancel')}</button>
                  <button className="btn btn--primary grow" disabled={busy} onClick={save}>{t('save')}</button>
                </div>
              </div>
            </Card>
          ) : (
            <button className="btn btn--secondary btn--block" onClick={() => setEditing(true)}>
              {t('editTransaction')}
            </button>
          )}

          {data && data.related.length > 0 && (
            <>
              <div className="section-title">{t('otherAtMerchant')}</div>
              <Card className="card--flush">
                {data.related.map((r) => (
                  <div key={r.id} className="tx" style={{ cursor: 'default' }}>
                    <span className="tx__body">
                      <span className="tx__meta">{formatDate(r.occurredAt, locale)}</span>
                    </span>
                    <span className="tx__amount">{money(r.amountMinor, tx.currency)}</span>
                  </div>
                ))}
              </Card>
            </>
          )}

          {/* Raw message, when retention has not yet purged it. */}
          {tx.raw_body && (
            <details>
              <summary className="small muted" style={{ cursor: 'pointer', padding: '8px 0' }}>
                {locale === 'ar' ? 'الرسالة الأصلية' : 'Original message'}
              </summary>
              <div className="mono small" style={{
                padding: 12, background: 'var(--bg-sunken)', borderRadius: 'var(--radius-sm)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 6,
              }}>{tx.raw_body}</div>
            </details>
          )}

          <button className="btn btn--danger btn--block" disabled={busy} onClick={remove}>{t('delete')}</button>
        </div>
      )}
    </Sheet>
  );
}

const PAYMENT_METHOD_LABELS: Record<string, { en: string; ar: string }> = {
  card: { en: 'Card', ar: 'بطاقة' },
  cash: { en: 'Cash', ar: 'نقداً' },
  bank_transfer: { en: 'Bank transfer', ar: 'تحويل بنكي' },
  direct_debit: { en: 'Direct debit', ar: 'خصم مباشر' },
  cheque: { en: 'Cheque', ar: 'شيك' },
  wallet: { en: 'Wallet', ar: 'محفظة' },
};

/**
 * Track this merchant as a subscription, or stop.
 *
 * It lives on the transaction because that is where a subscription is most
 * plainly a subscription — the charge itself — and because removing one was
 * reachable from the Plan tab while undoing it was reachable from nowhere. A
 * removal you cannot take back is not a removal, it is a mistake waiting to
 * be permanent.
 *
 * The frequency is asked in the same tap, for the same reason the Plan tab
 * asks it: a subscription without one cannot say when it renews, and guessing
 * monthly on a yearly plan is wrong by eleven months.
 */
function SubscriptionCard({ sub, merchantDisplay, onChanged }: {
  sub: MerchantSubscription;
  merchantDisplay: string;
  onChanged: () => void;
}): JSX.Element {
  const { locale, toast, toastError } = useApp();
  const ar = locale === 'ar';
  const [busy, setBusy] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);

  const EVERY: { key: string; en: string; ar: string }[] = [
    { key: 'monthly', en: 'Every month', ar: 'كل شهر' },
    { key: 'yearly', en: 'Every year', ar: 'كل سنة' },
    { key: 'weekly', en: 'Every week', ar: 'كل أسبوع' },
    { key: 'quarterly', en: 'Every 3 months', ar: 'كل 3 أشهر' },
  ];

  const path = `/subscriptions/${encodeURIComponent(sub.merchant)}`;

  const track = async (frequency: string) => {
    setBusy(frequency);
    try {
      await api.post(`${path}/answer`, { answer: 'recurring', frequency });
      toast(ar ? 'أُضيف إلى الاشتراكات' : 'Added to subscriptions');
      setChoosing(false);
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(null);
    }
  };

  const stop = async () => {
    setBusy('stop');
    try {
      // The same endpoint the Plan tab uses, so "remove" means one thing in
      // both places and this card can undo what either of them did.
      await api.post(`/recurring/${encodeURIComponent(sub.merchant)}/dismiss`, {});
      toast(ar ? 'أُزيل من الاشتراكات' : 'Removed from subscriptions');
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(null);
    }
  };

  const renews = sub.nextExpected
    ? new Date(sub.nextExpected).toLocaleDateString(ar ? 'ar' : 'en-GB',
        { day: 'numeric', month: 'short' })
    : null;

  return (
    <Card>
      <div className="field__label" style={{ marginBottom: 8 }}>
        {ar ? 'الاشتراك' : 'Subscription'}
      </div>

      {sub.state === 'tracked' ? (
        <>
          <div className="small" style={{ lineHeight: 1.6 }}>
            {ar
              ? `يُتابَع ${merchantDisplay} كاشتراك${renews ? ` · التجديد ${renews}` : ''}.`
              : `${merchantDisplay} is tracked as a subscription${renews ? ` · renews ${renews}` : ''}.`}
          </div>
          <button className="btn btn--ghost btn--block" style={{ marginTop: 10 }}
                  disabled={Boolean(busy)} onClick={stop}>
            {busy === 'stop' ? '…' : (ar ? 'ليس اشتراكاً' : 'Not a subscription')}
          </button>
        </>
      ) : choosing ? (
        <>
          <div className="small muted" style={{ lineHeight: 1.6, marginBottom: 10 }}>
            {ar ? `كم مرة يتكرر ${merchantDisplay}؟` : `How often does ${merchantDisplay} repeat?`}
          </div>
          <div className="stack stack--tight">
            {EVERY.map((f) => (
              <button key={f.key} className="btn btn--secondary btn--block"
                      disabled={Boolean(busy)} onClick={() => track(f.key)}>
                {busy === f.key ? '…' : (ar ? f.ar : f.en)}
              </button>
            ))}
            <button className="btn btn--ghost btn--block"
                    disabled={Boolean(busy)} onClick={() => setChoosing(false)}>
              {ar ? 'إلغاء' : 'Cancel'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="small muted" style={{ lineHeight: 1.6 }}>
            {/* Says which of the two "not a subscription" states this is, so
                the user can tell "I removed this" from "nothing has happened
                here yet" — and knows there is something to undo. */}
            {sub.state === 'dismissed'
              ? (ar
                  ? `أزلت ${merchantDisplay} من الاشتراكات. يمكنك إعادته.`
                  : `You removed ${merchantDisplay} from subscriptions. You can add it back.`)
              : sub.state === 'one_off'
                ? (ar
                    ? `سُجِّل ${merchantDisplay} كعملية لمرة واحدة.`
                    : `${merchantDisplay} was marked as a one-off.`)
                : (ar
                    ? 'لا يُتابَع هذا التاجر كاشتراك.'
                    : 'This merchant is not tracked as a subscription.')}
          </div>
          <button className="btn btn--secondary btn--block" style={{ marginTop: 10 }}
                  disabled={Boolean(busy)} onClick={() => setChoosing(true)}>
            {sub.state === 'untracked'
              ? (ar ? 'تتبَّعه كاشتراك' : 'Track as a subscription')
              : (ar ? 'أعِده إلى الاشتراكات' : 'Add back to subscriptions')}
          </button>
        </>
      )}
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="row row--between" style={{ paddingBlock: 7, gap: 16 }}>
      <span className="small muted" style={{ flexShrink: 0 }}>{label}</span>
      <span className="small" style={{ textAlign: 'end' }}>{value}</span>
    </div>
  );
}

// ── Manual add ───────────────────────────────────────────────────────────────

export function AddTransactionSheet({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }): JSX.Element {
  const { t, locale, user, categories, toast } = useApp();
  const currency = user?.currency ?? 'AED';

  const [tab, setTab] = useState<'manual' | 'paste'>('manual');
  const [amount, setAmount] = useState('');
  const [merchant, setMerchant] = useState('');
  const [type, setType] = useState('expense');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pasted, setPasted] = useState('');
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const saveManual = async () => {
    const amountMinor = parseAmountInput(amount, currency);
    if (!amountMinor) { setError(locale === 'ar' ? 'أدخل مبلغاً صحيحاً' : 'Enter a valid amount'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.post('/transactions', {
        amountMinor,
        transactionType: type,
        merchant: merchant || null,
        category: category || null,
        occurredAt: new Date(`${date}T12:00:00`).toISOString(),
      });
      toast(t('done'));
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    setBusy(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const res = await api.post<{ ok: boolean; reason?: string; parsed?: Record<string, unknown> }>(
        '/ingest/preview', { body: pasted },
      );
      if (!res.ok) setPreviewError(t('notATransaction'));
      else setPreview(res.parsed!);
    } catch (err) {
      setPreviewError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const savePasted = async () => {
    setBusy(true);
    try {
      await api.post('/ingest/message', { body: pasted, sourceType: 'api' });
      toast(t('done'));
      onSaved();
      onClose();
    } catch (err) {
      setPreviewError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={t('addTransaction')} onClose={onClose}>
      <div className="row" style={{ gap: 8, marginBottom: 16 }}>
        <button className={`chip${tab === 'manual' ? ' chip--active' : ''}`} onClick={() => setTab('manual')}>
          {t('sourceManual')}
        </button>
        <button className={`chip${tab === 'paste' ? ' chip--active' : ''}`} onClick={() => setTab('paste')}>
          {t('pasteMessage')}
        </button>
      </div>

      {tab === 'manual' ? (
        <div className="stack">
          <Field label={`${t('amount')} (${currency})`}>
            <input className="input" inputMode="decimal" value={amount} autoFocus
                   onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
          </Field>
          <Field label={t('merchant')}>
            <input className="input" value={merchant} onChange={(e) => setMerchant(e.target.value)}
                   placeholder={locale === 'ar' ? 'كارفور' : 'Carrefour'} />
          </Field>
          <Field label={t('type')}>
            <select className="select" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="expense">{t('typeExpense')}</option>
              <option value="income">{t('typeIncome')}</option>
              <option value="transfer">{t('typeTransfer')}</option>
              <option value="refund">{t('typeRefund')}</option>
              <option value="fee">{t('typeFee')}</option>
            </select>
          </Field>
          <Field label={t('category')}>
            <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">{locale === 'ar' ? 'دع فينتراك يقرر' : 'Let Fintrack decide'}</option>
              {categories.map((c) => (
                <option key={c.key} value={c.key}>{locale === 'ar' ? c.ar : c.en}</option>
              ))}
            </select>
          </Field>
          <Field label={t('date')} error={error}>
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <button className="btn btn--primary btn--block" disabled={busy} onClick={saveManual}>{t('save')}</button>
        </div>
      ) : (
        <div className="stack">
          <p className="small muted">{t('pasteHelp')}</p>
          <textarea className="textarea" value={pasted} onChange={(e) => { setPasted(e.target.value); setPreview(null); }}
                    placeholder="AED 127.50 was spent at STARBUCKS DUBAI…" />
          <button className="btn btn--secondary btn--block" disabled={busy || !pasted.trim()} onClick={runPreview}>
            {t('parseIt')}
          </button>

          {previewError && <div className="field__error">{previewError}</div>}

          {preview && (
            <>
              <Card>
                <div className="field__label" style={{ marginBottom: 8 }}>{t('parsedResult')}</div>
                {/* Not masked, unlike every other figure in the app. This one
                    is the app reading back a message the user pasted seconds
                    ago, and the whole point of showing it is to be checked
                    against what they pasted. Covered, the check cannot be made
                    — and amounts are hidden by default at every launch, so it
                    would be covered on the run that matters. Nothing about the
                    user's position is given away by a number they just
                    supplied. */}
                <DetailRow label={t('amount')} value={formatMoney(preview.amountMinor as number, preview.currency as string, { locale })} />
                <DetailRow label={t('merchant')} value={(preview.merchantDisplay as string) || '—'} />
                <DetailRow label={t('type')} value={preview.transactionType as string} />
                <DetailRow label={t('date')} value={formatDate(preview.occurredAt as string, locale)} />
                {Boolean(preview.cardLast4) && <DetailRow label={t('paymentMethod')} value={`•${preview.cardLast4}`} />}
                <div className="divider" style={{ marginBlock: 8 }} />
                <ConfidenceDot value={preview.confidence as number} />
                <ul className="tiny muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
                  {(preview.notes as string[]).map((n) => <li key={n}>· {n}</li>)}
                </ul>
              </Card>
              <button className="btn btn--primary btn--block" disabled={busy} onClick={savePasted}>
                {t('saveTransaction')}
              </button>
            </>
          )}
        </div>
      )}
    </Sheet>
  );
}

export { minorToInput };


/**
 * Two things a user knows about a charge that the bank message cannot say:
 * how much of it was actually theirs, and whether they are owed it back.
 */
function SplitAndRefundCard(
  { tx, expectations, onChanged }: {
    tx: Record<string, unknown>;
    expectations: ExpectedRefund[];
    onChanged: () => void;
  },
): JSX.Element {
  const { locale, toast, user, toastError } = useApp();
  const money = useMoney();
  const ar = locale === 'ar';
  const currency = (tx.currency as string) ?? user?.currency ?? 'AED';
  const amountMinor = Number(tx.amount_minor ?? 0);
  const id = tx.id as string;

  const [shareBps, setShareBps] = useState<number>(Number(tx.share_bps ?? 10000));
  const [busy, setBusy] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customPct, setCustomPct] = useState('');
  const [owedBy, setOwedBy] = useState('');
  const [naming, setNaming] = useState(false);

  // Read from the server rather than remembered in this component: the sheet is
  // unmounted every time it closes, and a card that forgot what it was already
  // watching offered to start watching it again.
  const openRefund = expectations.find((e) => e.kind === 'merchant' && !e.settledAt);
  const openShare = expectations.find((e) => e.kind === 'person' && !e.settledAt);

  const mine = Math.floor(amountMinor * shareBps / 10000);
  const theirs = amountMinor - mine;

  const setShare = async (bps: number) => {
    setBusy(true);
    try {
      await api.patch(`/transactions/${id}`, { shareBps: bps });
      setShareBps(bps);
      setCustomOpen(false);
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const applyCustom = async () => {
    const pct = Number(customPct.replace(',', '.'));
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast(ar ? 'أدخل نسبة بين 0 و100' : 'Enter a percentage between 0 and 100');
      return;
    }
    await setShare(Math.round(pct * 100));
  };

  const expectRefund = async () => {
    setBusy(true);
    try {
      await api.post(`/transactions/${id}/expect-refund`, {});
      toast(ar ? 'سنتابع وصول المبلغ' : 'We will watch for it');
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  /** Track the part of this charge that was not yours as owed back to you. */
  const trackOwed = async () => {
    setBusy(true);
    try {
      await api.post(`/transactions/${id}/expect-refund`, {
        kind: 'person', amountMinor: theirs, owedBy: owedBy.trim() || null,
      });
      toast(ar ? 'أُضيف إلى المستحق لك' : 'Added to what you are owed');
      setNaming(false);
      setOwedBy('');
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const settle = async (expectationId: string) => {
    setBusy(true);
    try {
      await api.post(`/refunds/expected/${expectationId}/settle`, {});
      toast(ar ? 'تم' : 'Done');
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const options: { bps: number; label: string }[] = [
    { bps: 10000, label: ar ? 'كله لي' : 'All mine' },
    { bps: 5000, label: ar ? 'النصف' : 'Half' },
    { bps: 3333, label: ar ? 'الثلث' : 'A third' },
  ];
  const isPreset = options.some((o) => o.bps === shareBps);

  return (
    <Card>
      <div className="field__label" style={{ marginBottom: 8 }}>
        {ar ? 'حصتي من هذه المعاملة' : 'My share of this'}
      </div>
      <div className="row row--wrap" style={{ gap: 8 }}>
        {options.map((o) => (
          <button
            key={o.bps}
            className={`btn btn--sm ${shareBps === o.bps && !customOpen ? 'btn--primary' : 'btn--secondary'}`}
            disabled={busy}
            onClick={() => setShare(o.bps)}
          >
            {o.label}
          </button>
        ))}
        <button
          className={`btn btn--sm ${(!isPreset || customOpen) ? 'btn--primary' : 'btn--secondary'}`}
          disabled={busy}
          onClick={() => { setCustomPct((shareBps / 100).toString()); setCustomOpen(!customOpen); }}
        >
          {!isPreset ? `${shareBps / 100}%` : (ar ? 'نسبة أخرى' : 'Custom')}
        </button>
      </div>

      {customOpen && (
        <div className="row" style={{ gap: 8, marginTop: 10 }}>
          <input
            className="input grow" type="text" inputMode="decimal"
            value={customPct} onChange={(e) => setCustomPct(e.target.value)}
            placeholder={ar ? 'النسبة %' : 'Percent'}
            aria-label={ar ? 'نسبتي %' : 'My percent'}
          />
          <button className="btn btn--primary" disabled={busy} onClick={applyCustom}>
            {ar ? 'تطبيق' : 'Apply'}
          </button>
        </div>
      )}

      {shareBps !== 10000 && (
        <div className="tiny muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
          {ar
            ? `يُحتسب ${money(mine, currency)} فقط في إجمالياتك وميزانياتك.`
            : `Only ${money(mine, currency)} counts towards your totals and budgets.`}
        </div>
      )}

      {/* The other side of a split. Splitting made the number right and the
          money invisible: the rest was neither your spending nor anything the
          app remembered you were owed. */}
      {shareBps !== 10000 && theirs > 0 && (
        <>
          <div className="divider" style={{ marginBlock: 12 }} />
          {openShare ? (
            <>
              <div className="small" style={{ lineHeight: 1.6 }}>
                {ar
                  ? `${money(openShare.amountMinor, openShare.currency)} مستحقة لك من ${openShare.owedBy || 'شخص ما'}.`
                  : `${money(openShare.amountMinor, openShare.currency)} is owed back to you by ${openShare.owedBy || 'someone'}.`}
              </div>
              <button className="btn btn--ghost btn--block btn--sm" style={{ marginTop: 8 }}
                      disabled={busy} onClick={() => settle(openShare.id)}>
                {ar ? 'استلمتها' : 'They paid me'}
              </button>
            </>
          ) : naming ? (
            <>
              <div className="small" style={{ lineHeight: 1.6, marginBottom: 8 }}>
                {ar
                  ? `من يدين لك بـ ${money(theirs, currency)}؟`
                  : `Who owes you the ${money(theirs, currency)}?`}
              </div>
              <div className="row" style={{ gap: 8 }}>
                <input className="input grow" type="text" value={owedBy}
                       onChange={(e) => setOwedBy(e.target.value)}
                       placeholder={ar ? 'الاسم (اختياري)' : 'Name (optional)'}
                       aria-label={ar ? 'من يدين لك' : 'Who owes you'} />
                <button className="btn btn--primary" disabled={busy} onClick={trackOwed}>
                  {ar ? 'تتبَّع' : 'Track'}
                </button>
              </div>
            </>
          ) : (
            <>
              <button className="btn btn--secondary btn--block btn--sm"
                      disabled={busy} onClick={() => setNaming(true)}>
                {ar
                  ? `شخص ما يدين لي بـ ${money(theirs, currency)}`
                  : `Someone owes me ${money(theirs, currency)}`}
              </button>
              <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.6 }}>
                {/* Offered rather than assumed: a share is often already
                    settled at the table, and a debt the app invented would
                    show up on the Plan tab and eventually be chased. */}
                {ar
                  ? 'لا نفترض ذلك تلقائياً — أحياناً تكون الحصة قد سُدِّدت وقتها. اضغط إن كانت لا تزال مستحقة، وسنتابعها.'
                  : 'Not assumed, since a share is often settled on the spot. Tap if it is still owed and we will watch for it.'}
              </div>
            </>
          )}
        </>
      )}

      <div className="divider" style={{ marginBlock: 12 }} />

      {openRefund ? (
        <>
          <div className="tiny muted">
            {ar
              ? `نتابع ${money(openRefund.amountMinor, openRefund.currency)} — متوقعة بحلول ${openRefund.expectedBy}.`
              : `Watching for ${money(openRefund.amountMinor, openRefund.currency)} — expected by ${openRefund.expectedBy}.`}
          </div>
          <button className="btn btn--ghost btn--block btn--sm" style={{ marginTop: 8 }}
                  disabled={busy} onClick={() => settle(openRefund.id)}>
            {ar ? 'وصل المبلغ' : 'It arrived'}
          </button>
        </>
      ) : (
        <>
          <button className="btn btn--secondary btn--block btn--sm" disabled={busy} onClick={expectRefund}>
            {ar ? 'أرجعت هذا — تابع المبلغ' : 'I returned this — watch for the refund'}
          </button>
          <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.6 }}>
            {ar
              ? 'المبالغ المسترجعة هي الجزء الذي لا يتحقق منه أحد. سنراقب وصولها.'
              : 'Refunds are the part nobody checks. We will watch for this one and say if it never lands.'}
          </div>
        </>
      )}
    </Card>
  );
}
